import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiCornerDownRight, FiMinus, FiPlus } from 'react-icons/fi';
import Styled from '../configuration-style';

/**
 * Folder component to render a folder structure
 * @param {object} props - The props object
 * @param {string} props.name - Name of the current field
 * @param {object} props.node - Current node data
 * @param {Function} props.setIsExpanded - Function to set the expanded state
 * @param {boolean} props.isExpanded - State to check if the folder is expanded
 * @param {number} props.level - Depth level of the folder
 * @param {boolean} [props.isFirst] - Flag to check if the folder is the first in the list
 * @param {Function} props.renderChildren - Function to render children nodes
 * @param {Function} props.isRequired - Function to check if the field is required
 * @param {string} props.parentOptionFolderName - Name of the parent option folder
 * @param {string}  props.partNumber - Part Number of folder
 * @param {Function} props.setExpandedParentOption - Function to set the expanded parent option
 * @returns {JSX.Element} The rendered Folder component
 */
const Folder = ({
  name,
  node,
  setIsExpanded,
  isExpanded,
  level,
  isFirst,
  renderChildren,
  isRequired,
  parentOptionFolderName,
  partNumber,
  setExpandedParentOption,
}) => {
  const { register, getValues, setValue } = useFormContext();

  const isChildrenSelected = (options) => {
    if (!options || !Array.isArray(options) || options.length === 0) {
      return false;
    }

    return options.some(
      (option) =>
        (option?.id !== null && option?.id !== undefined) ||
        (option?.children && isChildrenSelected(option.children)),
    );
  };

  const currentValues = getValues(name);
  const isValid = isChildrenSelected(currentValues?.children);

  useEffect(() => {
    if (isValid) {
      setValue(`${name}.id`, node.id);
    } else {
      setValue(`${name}.id`, null);
    }
  }, [isValid, setValue, node, name]);

  useEffect(() => {
    register(`${name}.is_required`, {
      value: node.is_required,
    });
    register(`${name}.parent_option_folder_name`, {
      value: parentOptionFolderName,
    });
    register(`${name}.partNumber`, {
      value: partNumber,
    });
  }, [name, node.is_required, parentOptionFolderName, register, partNumber]);

  return (
    <Styled.FolderContainer level={level * 20}>
      <Styled.Option
        onClick={() => {
          setIsExpanded(!isExpanded);
          setExpandedParentOption();
        }}
        level={level}
        isFirst={isFirst}
        isExpanded={isExpanded}
      >
        <span>
          {level !== 0 && <FiCornerDownRight />}
          <Styled.OptionName>
            {node.name}
            {isRequired()}
          </Styled.OptionName>
        </span>
        {isExpanded ? <FiMinus /> : <FiPlus />}
      </Styled.Option>
      {renderChildren(parentOptionFolderName || name)}
    </Styled.FolderContainer>
  );
};

Folder.propTypes = {
  name: PropTypes.string.isRequired,
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    is_required: PropTypes.bool,
  }).isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  level: PropTypes.number.isRequired,
  isFirst: PropTypes.bool,
  renderChildren: PropTypes.func.isRequired,
  isRequired: PropTypes.func.isRequired,
  parentOptionFolderName: PropTypes.string.isRequired,
  partNumber: PropTypes.string.isRequired,
  setExpandedParentOption: PropTypes.func,
};

Folder.defaultProps = {
  isFirst: false,
  setExpandedParentOption: () => {},
};

export default Folder;
